/**
 * Utility functions for managing SEO meta tags and structured data (JSON-LD)
 */

export function setMetaTag(name, content, attr = 'name') {
    if (!content) return;
    let element = document.querySelector(`meta[${attr}="${name}"]`);
    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, name);
        document.head.appendChild(element);
    }
    element.setAttribute('content', content);
}

export function setCanonicalUrl(url) {
    let cleanUrl = url;
    if (!cleanUrl && typeof window !== 'undefined') {
        cleanUrl = window.location.origin + window.location.pathname;
    }
    let element = document.querySelector('link[rel="canonical"]');
    if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', 'canonical');
        document.head.appendChild(element);
    }
    element.setAttribute('href', cleanUrl || 'https://bib-job.com/');
}

export function updatePageSeo({
    title,
    description,
    image,
    url,
    type = 'website'
}) {
    const defaultTitle = 'BIB JOB - မြန်မာနိုင်ငံရှိ အကောင်းဆုံး အလုပ်အကိုင်များ ရှာဖွေရန် | Best Jobs in Myanmar';
    const defaultDesc = 'Find the best jobs in Myanmar at BIB JOB. မြန်မာနိုင်ငံရှိ အကောင်းဆုံး အလုပ်အကိုင်အခွင့်အလမ်းများကို ရှာဖွေလျှောက်ထားလိုက်ပါ။ Search, apply, and get hired today!';

    const pageTitle = title ? (title.includes('BIB JOB') ? title : `${title} | BIB JOB`) : defaultTitle;
    const pageDesc = description || defaultDesc;
    const pageUrl = url || (typeof window !== 'undefined' ? (window.location.origin + window.location.pathname) : 'https://bib-job.com/');
    let pageImg = image || 'https://bib-job.com/job_logo_1200.png';
    if (pageImg && pageImg.includes('/uploads/') && !pageImg.includes('/backend/uploads/')) {
        pageImg = pageImg.replace('/uploads/', '/backend/uploads/');
    }

    document.title = pageTitle;

    // Standard Meta Tags
    setMetaTag('description', pageDesc);
    setMetaTag('robots', 'index, follow');

    // OpenGraph Tags
    setMetaTag('og:type', type, 'property');
    setMetaTag('og:title', pageTitle, 'property');
    setMetaTag('og:description', pageDesc, 'property');
    setMetaTag('og:url', pageUrl, 'property');
    setMetaTag('og:image', pageImg, 'property');
    setMetaTag('og:image:secure_url', pageImg, 'property');
    setMetaTag('og:site_name', 'BIB Job', 'property');
    setMetaTag('og:locale', 'my_MM', 'property');

    // Twitter Card Tags
    setMetaTag('twitter:title', pageTitle);
    setMetaTag('twitter:description', pageDesc);
    setMetaTag('twitter:image', pageImg);
    setMetaTag('twitter:card', pageImg.includes('job_logo_1200.png') ? 'summary_large_image' : 'summary');

    // Canonical URL
    setCanonicalUrl(pageUrl);
}

const JOBPOSTING_SCRIPT_ID = 'google-jobposting-jsonld';
const BREADCRUMB_SCRIPT_ID = 'google-breadcrumb-jsonld';
const WEBSITE_SCRIPT_ID = 'google-website-jsonld';

export function injectJobPostingSchema(jobInfo) {
    if (!jobInfo || !jobInfo.id) return;

    removeJobPostingSchema();

    const script = document.createElement('script');
    script.id = JOBPOSTING_SCRIPT_ID;
    script.type = 'application/ld+json';

    let employmentType = 'FULL_TIME';
    if (jobInfo.job_type == '2') employmentType = 'PART_TIME';
    else if (jobInfo.job_type == '3') employmentType = 'INTERN';
    else if (jobInfo.job_type == '4') employmentType = 'CONTRACT';
    else if (jobInfo.job_type == '5') employmentType = 'TELECOMMUTE';

    const postalCodes = {
        0: "01011",
        1: "09011",
        2: "13011",
        3: "03011",
        4: "02011",
        5: "14011",
        6: "15011",
        7: "08011",
        8: "12011",
        9: "04011",
        10: "05011",
        11: "06011",
        12: "11181",
        13: "07011",
        14: "10011"
    };

    const regionName = jobInfo.region_name || jobInfo.region || 'Yangon';
    const locality = jobInfo.town || regionName || 'Yangon';
    const streetAddr = jobInfo.address || `${locality}, ${regionName}`;
    const postal = jobInfo.postal_code || postalCodes[jobInfo.region] || '11181';

    const schemaData = {
        '@context': 'https://schema.org/',
        '@type': 'JobPosting',
        'title': jobInfo.job_title || '',
        'description': jobInfo.description || jobInfo.short_desc || jobInfo.job_title || '',
        'identifier': {
            '@type': 'PropertyValue',
            'name': 'BIB JOB',
            'value': `JOB-${jobInfo.id}`
        },
        'datePosted': jobInfo.posted_date || jobInfo.created_at || new Date().toISOString().split('T')[0],
        'employmentType': employmentType,
        'directApply': true,
        'url': window.location.href,
        'hiringOrganization': {
            '@type': 'Organization',
            'name': jobInfo.company_name || 'BIB Job',
            'logo': jobInfo.company_logo_path || 'https://bib-job.com/job_logo_1200.png',
            'sameAs': `https://bib-job.com/job_listing/company_detail/${jobInfo.company_id || ''}`
        },
        'jobLocation': {
            '@type': 'Place',
            'address': {
                '@type': 'PostalAddress',
                'streetAddress': streetAddr,
                'addressLocality': locality,
                'addressRegion': regionName,
                'postalCode': postal,
                'addressCountry': 'MM'
            }
        }
    };

    if (jobInfo.experience) {
        schemaData.experienceRequirements = jobInfo.experience;
    }

    if (jobInfo.qualification) {
        schemaData.qualifications = jobInfo.qualification;
    }

    if (jobInfo.category_name) {
        schemaData.occupationalCategory = jobInfo.category_name;
    }

    if (jobInfo.job_type == '5') {
        schemaData.jobLocationType = 'TELECOMMUTE';
        schemaData.applicantLocationRequirements = {
            '@type': 'Country',
            'name': 'Myanmar'
        };
    }

    if (jobInfo.expired_date) {
        schemaData.validThrough = new Date(jobInfo.expired_date).toISOString();
    }

    const minSal = parseInt(jobInfo.min_salary) || 0;
    const maxSal = parseInt(jobInfo.max_salary) || 0;

    if (minSal > 0 || maxSal > 0) {
        const salaryVal = {
            '@type': 'QuantitativeValue',
            'unitText': 'MONTH'
        };

        if (minSal > 0 && maxSal > 0 && minSal < maxSal) {
            salaryVal.minValue = minSal;
            salaryVal.maxValue = maxSal;
        } else if (minSal > 0 && maxSal > 0 && minSal === maxSal) {
            salaryVal.value = minSal;
        } else if (minSal > 0) {
            salaryVal.value = minSal;
        } else if (maxSal > 0) {
            salaryVal.value = maxSal;
        }

        schemaData.baseSalary = {
            '@type': 'MonetaryAmount',
            'currency': 'MMK',
            'value': salaryVal
        };
    }

    script.textContent = JSON.stringify(schemaData, null, 2);
    document.head.appendChild(script);
}

export function injectBreadcrumbSchema(items = []) {
    if (!items || items.length === 0) return;

    removeBreadcrumbSchema();

    const script = document.createElement('script');
    script.id = BREADCRUMB_SCRIPT_ID;
    script.type = 'application/ld+json';

    const schemaData = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': items.map((item, index) => ({
            '@type': 'ListItem',
            'position': index + 1,
            'name': item.name,
            'item': item.url
        }))
    };

    script.textContent = JSON.stringify(schemaData, null, 2);
    document.head.appendChild(script);
}

export function injectWebSiteSchema() {
    if (document.getElementById(WEBSITE_SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = WEBSITE_SCRIPT_ID;
    script.type = 'application/ld+json';

    const schemaData = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        'name': 'BIB JOB',
        'url': 'https://bib-job.com/',
        'potentialAction': {
            '@type': 'SearchAction',
            'target': {
                '@type': 'EntryPoint',
                'urlTemplate': 'https://bib-job.com/search/result?keyword={search_term_string}'
            },
            'query-input': 'required name=search_term_string'
        }
    };

    script.textContent = JSON.stringify(schemaData, null, 2);
    document.head.appendChild(script);
}

export function removeJobPostingSchema() {
    const script = document.getElementById(JOBPOSTING_SCRIPT_ID);
    if (script) script.remove();
}

export function removeBreadcrumbSchema() {
    const script = document.getElementById(BREADCRUMB_SCRIPT_ID);
    if (script) script.remove();
}